// Athlete-side credentials editor (equestrian, Tier 3 SP4). Add/edit/delete
// membership / safety / eligibility / rating facts against /api/credentials.
// Mirrors TrackMarksEditor: self-contained fetch + form + list, with a
// ConfirmModal when editing a verified row (the backend clears the blue dot on
// any content edit). Rendered by PlayerStatsPage for equestrian athletes only.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import Button from '../Button';
import ConfirmModal from '../ConfirmModal';
import VerifiedFlame from '../VerifiedFlame';
import { CREDENTIAL_TYPE_OPTIONS, CREDENTIAL_TYPE_LABEL } from '../../lib/equestrian';
import { resolveGetConfig } from './editorConfig';
import './AthleteEditor.css';

const SPORT_SLUG = 'equestrian';

const EMPTY_CREDENTIAL = {
  credential_type: 'membership', title: '', issuer: '', status: '',
  identifier: '', issued_date: '', expires_date: '',
};

export default function CredentialsEditor({ getConfig: getConfigProp }) {
  const { API_URL } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [form, setForm] = useState(EMPTY_CREDENTIAL);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const getConfig = resolveGetConfig(getConfigProp);

  useEffect(() => { fetchCredentials(); }, []);

  const fetchCredentials = async () => {
    try {
      const res = await axios.get(`${API_URL}/credentials/me`, getConfig());
      setCredentials(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching credentials:', err);
    }
  };

  const showMsg = (text, type = '') => {
    setMessage(text); setMessageType(type);
    setTimeout(() => setMessage(''), type === 'error' ? 4000 : 3000);
  };

  const setField = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const buildPayload = () => ({
    sport_slug: SPORT_SLUG,
    credential_type: form.credential_type,
    title: form.title.trim(),
    issuer: form.issuer.trim() || null,
    status: form.status.trim() || null,
    identifier: form.identifier.trim() || null,
    issued_date: form.issued_date || null,
    expires_date: form.expires_date || null,
  });

  const doSave = async () => {
    const payload = buildPayload();
    try {
      if (editingId) {
        await axios.patch(`${API_URL}/credentials/me/${editingId}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/credentials/me`, payload, getConfig());
      }
      resetForm();
      fetchCredentials();
      showMsg(editingId ? 'Credential updated' : 'Credential added');
    } catch (err) {
      showMsg('Error saving credential', 'error');
    }
  };

  const saveCredential = () => {
    if (!form.credential_type) { showMsg('Pick a type', 'error'); return; }
    if (!form.title.trim()) { showMsg('Enter a title', 'error'); return; }
    // Editing a verified credential clears its verification on the backend.
    const editingVerified = editingId && credentials.find(c => c.id === editingId)?.verified;
    if (editingVerified) {
      setConfirmAction({
        title: 'Unverify Credential',
        message: 'This credential is currently verified. Editing it will clear the verification, and it can only be re-verified by ProZpects staff or a Partner.',
        confirmLabel: 'Continue', danger: true,
        onConfirm: () => { setConfirmAction(null); doSave(); },
        onCancel: () => setConfirmAction(null),
      });
      return;
    }
    doSave();
  };

  const resetForm = () => { setForm(EMPTY_CREDENTIAL); setEditingId(null); };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      credential_type: c.credential_type || 'membership',
      title: c.title || '',
      issuer: c.issuer || '',
      status: c.status || '',
      identifier: c.identifier || '',
      issued_date: c.issued_date || '',
      expires_date: c.expires_date || '',
    });
  };

  const deleteCredential = async (id) => {
    try {
      await axios.delete(`${API_URL}/credentials/me/${id}`, getConfig());
      if (editingId === id) resetForm();
      fetchCredentials();
      showMsg('Credential removed');
    } catch (err) {
      console.error('Error deleting credential:', err);
    }
  };

  // Group credentials by type, ordered to match the picklist.
  const grouped = useMemo(() => {
    const order = new Map(CREDENTIAL_TYPE_OPTIONS.map((o, i) => [o.value, i]));
    const byType = new Map();
    for (const c of credentials) {
      const t = c.credential_type || '';
      if (!byType.has(t)) byType.set(t, []);
      byType.get(t).push(c);
    }
    return [...byType.entries()]
      .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
      .map(([type, rows]) => ({ type, label: CREDENTIAL_TYPE_LABEL[type] || type || 'Other', rows }));
  }, [credentials]);

  return (
    <>
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      <div className="settings-section">
        <h2 className="settings-section__title">Credentials</h2>
        <p className="settings-section__desc">
          Log membership, safety, eligibility, and rating credentials — USEF membership,
          SafeSport certification, NCAA amateur status, governing-body ratings, and the like.
        </p>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label className="form-field__label">Type</label>
            <select
              className="form-field__select"
              value={form.credential_type}
              onChange={(e) => setField({ credential_type: e.target.value })}
              data-testid="credential_type"
            >
              {CREDENTIAL_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label">Title</label>
            <input
              type="text"
              className="form-field__input"
              value={form.title}
              onChange={(e) => setField({ title: e.target.value })}
              placeholder="e.g., USEF Competing Membership"
              data-testid="credential_title"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label">Issuer</label>
            <input
              type="text"
              className="form-field__input"
              value={form.issuer}
              onChange={(e) => setField({ issuer: e.target.value })}
              placeholder="e.g., US Equestrian"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Status</label>
            <input
              type="text"
              className="form-field__input"
              value={form.status}
              onChange={(e) => setField({ status: e.target.value })}
              placeholder="e.g., Active"
            />
          </div>
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Identifier</label>
            <input
              type="text"
              className="form-field__input"
              value={form.identifier}
              onChange={(e) => setField({ identifier: e.target.value })}
              placeholder="e.g., member #"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Issued</label>
            <input
              type="date"
              className="form-field__input"
              value={form.issued_date}
              onChange={(e) => setField({ issued_date: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ maxWidth: 180 }}>
            <label className="form-field__label">Expires</label>
            <input
              type="date"
              className="form-field__input"
              value={form.expires_date}
              onChange={(e) => setField({ expires_date: e.target.value })}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="accent" size="sm" onClick={saveCredential} data-testid="credential_save">
            {editingId ? 'Save Changes' : 'Add Credential'}
          </Button>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>
          )}
        </div>

        {credentials.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: 12 }}>No credentials logged yet.</p>
        )}
        {grouped.map(g => (
          <div key={g.type} className="ae-mark-group" style={{ marginTop: 14 }}>
            <div className="ae-mark-group__head">{g.label}</div>
            {g.rows.map(c => (
              <div key={c.id} className="accolade-row">
                <div className="accolade-row__body">
                  <span className="accolade-row__title">
                    {c.title}
                    {c.verified && <VerifiedFlame />}
                  </span>
                  <span className="accolade-row__meta">
                    {c.issuer && <span className="accolade-row__src">{c.issuer}</span>}
                    {c.status && <span className="accolade-chip">{c.status}</span>}
                    {c.identifier && <span className="accolade-row__src">#{c.identifier}</span>}
                    {c.issued_date && <span className="accolade-row__src">Issued {c.issued_date}</span>}
                    {c.expires_date && <span className="accolade-row__src">Expires {c.expires_date}</span>}
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(c)}>Edit</Button>
                <Button type="button" variant="danger" size="sm" onClick={() => deleteCredential(c.id)}>Delete</Button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </>
  );
}
